import { Lookup } from '@/types/lookup';
import { artiaProtectedApi } from '@/lib/api-client';
import { deleteWithValidation, downloadFile, postWithValidation, putWithValidation } from '@/lib/api-helpers';
import { buildQueryString } from '@/utils/filtering-functions';

import { StateFilterData } from '../components';
import { ColumnConfiguration, ColumnConfigurationGroup, CustomState, McoCarveOutMethod,StateDrugOrClass } from '../types';

export type ColumnGroupsResponse = {
  customReports: { id: number, name: string, columns: Lookup<string>[] }[];
  systemReports: Record<ColumnConfigurationGroup, { description: string, columnConfigurations: ColumnConfiguration[] }>;
};

export const useStatesService = () => {
  const getColumnGroups = (): Promise<ColumnGroupsResponse> => {
    return artiaProtectedApi.get('states/groups');
  };

  const importStatesData = async (data: FormData) => {
    await postWithValidation('states/import', { data });
  };

  const importStateLives = async (data: FormData) => {
    await postWithValidation('states/import-state-lives', { data });
  };

  const exportStatesToCsv = async (
    groups: ColumnConfigurationGroup[],
    stateCodes: string[],
    clinicalsProducts: number[],
    classifications: number[]
  ) => {
    return downloadFile(
      'GET',
      `states/export${buildQueryString({ groups, stateCodes, clinicalsProducts, classifications })}`
    );
  };

  const exportHistoricalStateLivesToCsv = async (stateCodes: string[], startDate?: string, endDate?: string) => {
    return downloadFile(
      'GET',
      `states/export-historical-state-lives${buildQueryString({ stateCodes, startDate, endDate })}`
    );
  };

  const exportHistoricalContactsToCsv = async (stateCodes: string[], startDate?: string, endDate?: string) => {
    return downloadFile(
      'GET',
      `states/export-historical-state-contacts${buildQueryString({ stateCodes, startDate, endDate })}`
    );
  };

  const lookupStatesWithColumnGroup = (columnGroup: ColumnConfigurationGroup, filters: StateFilterData): Promise<{ columns: ColumnConfiguration[], states: CustomState[] }> => {
    const {
      carvedOutClassifications,
      carvedOutClinicalsProducts,
      exemptClinicalsProducts,
      exemptClassifications,
      ...rest
    } = filters;

    // @ts-expect-error TS(2345): Argument of type '{ carvedOutClassifications: numb... Remove this comment to see the full error message
    return artiaProtectedApi.get(`states/with-custom-columns${buildQueryString({
      groups: columnGroup,
      ...rest,
      carvedOutClassifications: carvedOutClassifications.map(it => it.id),
      carvedOutClinicalsProducts: carvedOutClinicalsProducts.map(it => it.id),
      exemptClinicalsProducts: exemptClinicalsProducts.map(it => it.id),
      exemptClassifications: exemptClassifications.map(it => it.id),
    })}`);
  };

  const getMcoCarveOuts = (): Promise<Record<string, StateDrugOrClass[]>> => {
    return artiaProtectedApi.get('states/mco-carve-outs');
  };

  const createMcoCarveOut = (stateCode: string, carveOutMethod: McoCarveOutMethod, associationIdList: Lookup[]): Promise<void> => {
    return postWithValidation(`states/${stateCode}/mco-carve-outs`, {
      data: { carveOutMethod, associationIdList: associationIdList.map(it => it.id) }
    });
  };

  const deleteMcoCarveOut = (carveOutId: number): Promise<void> => {
    return deleteWithValidation(`states/mco-carve-outs/${carveOutId}`);
  };

  const getPdlExemptions = (): Promise<Record<string, StateDrugOrClass[]>> => {
    return artiaProtectedApi.get('states/pdl-exemptions');
  };

  const createPdlExemption = (stateCode: string, carveOutMethod: McoCarveOutMethod, associationIdList: Lookup[]): Promise<void> => {
    return postWithValidation(`states/${stateCode}/pdl-exemptions`, {
      data: { carveOutMethod, associationIdList: associationIdList.map(it => it.id) }
    });
  };

  const deletePdlExemption = (carveOutId: number): Promise<void> => {
    return deleteWithValidation(`states/pdl-exemptions/${carveOutId}`);
  };

  const updateStateData = (stateCode: string, updatedColumns: Record<string, string>) => {
    return postWithValidation('states/update', {
      data: { stateCode, updatedColumns }
    });
  };

  const createNewCustomReport = async (name: string, columns: Lookup<string>[]) => {
    return postWithValidation('custom-state-reports', {
      data: { name, columns: columns.map(it => it.id) }
    });
  };

  const updateCustomReport = async (id: number, name: string, columns: Lookup<string>[]) => {
    return putWithValidation(`custom-state-reports/${id}`, {
      data: { name, columns: columns.map(it => it.id) }
    });
  };

  const deleteCustomReport = async (id: number) => {
    return deleteWithValidation(`custom-state-reports/${id}`);
  };

  return {
    getColumnGroups,
    importStatesData,
    importStateLives,
    exportStatesToCsv,
    exportHistoricalStateLivesToCsv,
    exportHistoricalContactsToCsv,
    lookupStatesWithColumnGroup,
    getMcoCarveOuts,
    createMcoCarveOut,
    deleteMcoCarveOut,
    getPdlExemptions,
    createPdlExemption,
    deletePdlExemption,
    updateStateData,
    createNewCustomReport,
    updateCustomReport,
    deleteCustomReport
  };
};
